import React, { useEffect, useRef, useState } from "react";

/** Tiny className combiner (no external deps). */
export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** Centered, responsive content width used across the whole site. */
export const Container: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className, children }) => (
  <div className={cn("container mx-auto", className)}>{children}</div>
);

/** A vertical page section with consistent rhythm. */
export const Section: React.FC<{
  id?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ id, className, children }) => (
  <section id={id} className={cn("py-20 md:py-28", className)}>
    {children}
  </section>
);

/** Small uppercase label / chip. */
export const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-brand-400",
      className
    )}
  >
    {children}
  </span>
);

/** Section heading block: eyebrow + title + optional intro, center or left. */
export const SectionHeading: React.FC<{
  eyebrow?: string;
  title: React.ReactNode;
  intro?: React.ReactNode;
  align?: "center" | "left";
  className?: string;
}> = ({ eyebrow, title, intro, align = "center", className }) => (
  <div
    className={cn(
      "max-w-2xl",
      align === "center" ? "mx-auto text-center" : "text-left",
      className
    )}
  >
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl md:text-[2.75rem]">
      {title}
    </h2>
    {intro && (
      <p className="mt-4 text-base leading-relaxed text-zinc-400 sm:text-lg">
        {intro}
      </p>
    )}
  </div>
);

/** Frosted card surface. */
export const Card: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className, children }) => (
  <div
    className={cn(
      "rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm",
      className
    )}
  >
    {children}
  </div>
);

type ButtonProps = {
  href: string;
  children: React.ReactNode;
  variant?: "primary" | "ghost";
  external?: boolean;
  className?: string;
};

/** Link styled as a button. Use `external` for off-site links. */
export const Button: React.FC<ButtonProps> = ({
  href,
  children,
  variant = "primary",
  external,
  className,
}) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5";
  const styles =
    variant === "primary"
      ? "bg-brand-500 text-ink-950 hover:bg-brand-400 shadow-glow"
      : "border border-white/15 text-white hover:border-brand-500/50 hover:text-brand-400";
  const extra = external
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <a href={href} className={cn(base, styles, className)} {...extra}>
      {children}
    </a>
  );
};

/** Animated count-up stat. */
export const Stat: React.FC<{
  value: string;
  label: string;
  accent?: "brand" | "gold";
}> = ({ value, label, accent = "brand" }) => (
  <div className="text-center">
    <div
      className={cn(
        "font-display text-3xl font-bold sm:text-4xl md:text-5xl",
        accent === "gold" ? "text-gold-400" : "text-brand-400"
      )}
    >
      {value}
    </div>
    <div className="mt-2 text-xs uppercase tracking-wider text-zinc-400 sm:text-sm">
      {label}
    </div>
  </div>
);

/**
 * Reveals children with a fade-up when scrolled into view.
 * Adds a small stagger via `delay` (ms).
 */
export const Reveal: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn("reveal", visible && "is-visible", className)}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};
